import { Injectable } from '@angular/core';
import { forkJoin, map, of, switchMap } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';

export type CrmFeature = 'leads' | 'applicants';

export type LeadStatusDto = {
  _id: string;
  code: string;
  name: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  isSystem: boolean;
  crmFeatures?: CrmFeature;
};

export type LeadStatusPayload = {
  code: string;
  name: string;
  description?: string;
  displayOrder: number;
};

export type LeadStatusUpdatePayload = {
  name?: string;
  description?: string;
  displayOrder?: number;
  isActive?: boolean;
};

export type LeadStatusTransitionDto = {
  _id: string;
  fromStatusId: string;
  toStatusId: string | LeadStatusDto;
  transitionName: string;
  description?: string;
  requiresComment: boolean;
  isActive: boolean;
  crmFeatures?: CrmFeature;
};

export type LeadStatusDetailDto = LeadStatusDto & {
  transitions: LeadStatusTransitionDto[];
};

export type CreateTransitionPayload = {
  fromStatusId: string;
  toStatusId: string;
  transitionName: string;
  description?: string;
  requiresComment?: boolean;
};

export type UpdateTransitionPayload = {
  transitionName?: string;
  description?: string;
  requiresComment?: boolean;
  isActive?: boolean;
};

export type TransitionOverviewRow = {
  fromName: string;
  fromCode: string;
  toName: string;
  toCode: string;
  transitionName: string;
  requiresComment: boolean;
};

export type WorkflowTransitionEdge = {
  id: string;
  source: string;
  target: string;
  transitionName: string;
  requiresComment: boolean;
};

export type WorkflowGraphData = {
  statuses: LeadStatusDto[];
  transitions: WorkflowTransitionEdge[];
};

export type LeadSubStatusDto = {
  _id: string;
  name: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  isSystem: boolean;
  crmFeatures?: CrmFeature;
};

export type LeadSubStatusPayload = {
  name: string;
  description?: string;
  displayOrder: number;
  isActive?: boolean;
};

export type LeadSubStatusUpdatePayload = {
  name?: string;
  description?: string;
  displayOrder?: number;
  isActive?: boolean;
};

@Injectable({ providedIn: 'root' })
export class LeadStatusesService {
  constructor(private readonly api: ApiService) {}

  private statusBasePath(feature: CrmFeature): string {
    return feature === 'applicants' ? 'platform/crm/applicant-statuses' : 'platform/crm/lead-statuses';
  }

  list(feature: CrmFeature = 'leads') {
    return this.api
      .get<ApiEnvelope<LeadStatusDto[]>>(this.statusBasePath(feature))
      .pipe(map((r) => r.data));
  }

  getById(id: string, feature: CrmFeature = 'leads') {
    return this.api
      .get<ApiEnvelope<LeadStatusDetailDto>>(`${this.statusBasePath(feature)}/${id}`)
      .pipe(map((r) => r.data));
  }

  create(payload: LeadStatusPayload, feature: CrmFeature = 'leads') {
    return this.api
      .post<ApiEnvelope<LeadStatusDto>>(this.statusBasePath(feature), payload)
      .pipe(map((r) => r.data));
  }

  update(id: string, payload: LeadStatusUpdatePayload, feature: CrmFeature = 'leads') {
    return this.api
      .patch<ApiEnvelope<LeadStatusDto>>(`${this.statusBasePath(feature)}/${id}`, payload)
      .pipe(map((r) => r.data));
  }

  remove(id: string, feature: CrmFeature = 'leads') {
    return this.api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`${this.statusBasePath(feature)}/${id}`)
      .pipe(map((r) => r.data));
  }

  listTransitions(statusId: string, feature: CrmFeature = 'leads') {
    return this.api
      .get<ApiEnvelope<LeadStatusTransitionDto[]>>(
        `${this.statusBasePath(feature)}/${statusId}/transitions`,
      )
      .pipe(map((r) => r.data));
  }

  addTransition(payload: CreateTransitionPayload) {
    return this.api
      .post<ApiEnvelope<LeadStatusTransitionDto>>('platform/crm/transitions', payload)
      .pipe(map((r) => r.data));
  }

  updateTransition(id: string, payload: UpdateTransitionPayload) {
    return this.api
      .patch<ApiEnvelope<LeadStatusTransitionDto>>(`platform/crm/transitions/${id}`, payload)
      .pipe(map((r) => r.data));
  }

  removeTransition(id: string) {
    return this.api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`platform/crm/transitions/${id}`)
      .pipe(map((r) => r.data));
  }

  listAllTransitionsOverview(feature: CrmFeature = 'leads') {
    return this.list(feature).pipe(
      switchMap((statuses) => {
        if (statuses.length === 0) return of([] as TransitionOverviewRow[]);

        const statusById = new Map(statuses.map((s) => [s._id, s]));

        return forkJoin(
          statuses.map((from) =>
            this.listTransitions(from._id, feature).pipe(
              map((transitions) =>
                transitions.flatMap((t) => {
                  const to =
                    typeof t.toStatusId === 'object' && t.toStatusId !== null
                      ? t.toStatusId
                      : statusById.get(String(t.toStatusId));
                  if (!to) return [];
                  return [
                    {
                      fromName: from.name,
                      fromCode: from.code,
                      toName: to.name,
                      toCode: to.code,
                      transitionName: t.transitionName,
                      requiresComment: t.requiresComment,
                    },
                  ];
                }),
              ),
            ),
          ),
        ).pipe(
          map((groups) => {
            const rows = groups.flat();
            return rows.sort((a, b) => {
              const fromA = statuses.find((s) => s.code === a.fromCode)?.displayOrder ?? 0;
              const fromB = statuses.find((s) => s.code === b.fromCode)?.displayOrder ?? 0;
              if (fromA !== fromB) return fromA - fromB;
              const toA = statuses.find((s) => s.code === a.toCode)?.displayOrder ?? 0;
              const toB = statuses.find((s) => s.code === b.toCode)?.displayOrder ?? 0;
              return toA - toB;
            });
          }),
        );
      }),
    );
  }

  listWorkflowGraph(feature: CrmFeature = 'leads') {
    return this.list(feature).pipe(
      switchMap((statuses) => {
        const activeStatuses = statuses.filter((s) => s.isActive);
        if (activeStatuses.length === 0) {
          return of({ statuses: [], transitions: [] } as WorkflowGraphData);
        }

        const statusById = new Map(activeStatuses.map((s) => [s._id, s]));
        return forkJoin(
          activeStatuses.map((from) =>
            this.listTransitions(from._id, feature).pipe(
              map((transitions) =>
                transitions.flatMap((t) => {
                  const toId =
                    typeof t.toStatusId === 'object' && t.toStatusId !== null
                      ? t.toStatusId._id
                      : String(t.toStatusId);
                  const toStatus = statusById.get(toId);
                  if (!toStatus) return [];
                  return [
                    {
                      id: t._id,
                      source: from._id,
                      target: toStatus._id,
                      transitionName: t.transitionName,
                      requiresComment: t.requiresComment,
                    } as WorkflowTransitionEdge,
                  ];
                }),
              ),
            ),
          ),
        ).pipe(
          map((groups) => ({
            statuses: activeStatuses,
            transitions: groups.flat(),
          })),
        );
      }),
    );
  }

  private subStatusBasePath(feature: CrmFeature): string {
    return feature === 'applicants' ? 'platform/crm/applicant-sub-statuses' : 'platform/crm/lead-sub-statuses';
  }

  listSubStatuses(feature: CrmFeature = 'leads') {
    return this.api
      .get<ApiEnvelope<LeadSubStatusDto[]>>(this.subStatusBasePath(feature))
      .pipe(map((r) => r.data));
  }

  createSubStatus(payload: LeadSubStatusPayload, feature: CrmFeature = 'leads') {
    return this.api
      .post<ApiEnvelope<LeadSubStatusDto>>(this.subStatusBasePath(feature), payload)
      .pipe(map((r) => r.data));
  }

  updateSubStatus(id: string, payload: LeadSubStatusUpdatePayload, feature: CrmFeature = 'leads') {
    return this.api
      .patch<ApiEnvelope<LeadSubStatusDto>>(`${this.subStatusBasePath(feature)}/${id}`, payload)
      .pipe(map((r) => r.data));
  }

  removeSubStatus(id: string, feature: CrmFeature = 'leads') {
    return this.api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`${this.subStatusBasePath(feature)}/${id}`)
      .pipe(map((r) => r.data));
  }

  reorderSubStatuses(orderedIds: string[], feature: CrmFeature = 'leads') {
    return this.api
      .patch<ApiEnvelope<LeadSubStatusDto[]>>(`${this.subStatusBasePath(feature)}/reorder`, { orderedIds })
      .pipe(map((r) => r.data));
  }
}
